import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';


import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Users,
  Edit,
  Check,
  Calendar,
  FileText,
  Download,
  Eye,
  Paperclip,
  IdCard,
  Home,
  Receipt,
  Building,
  Heart
} from 'lucide-react';
import { formatCpfCnpj, formatPhone } from '@/utils/format';

interface ClientePreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: any;
  onConfirm: () => void;
  onEdit: () => void;
  documentos?: any[];
  getDocumentUrl?: (path: string) => Promise<string | null>;
}

export const ClientePreview: React.FC<ClientePreviewProps> = ({
  open,
  onOpenChange,
  formData,
  onConfirm,
  onEdit,
  documentos = [],
  getDocumentUrl
}) => {
  const formatDate = (date: string | Date) => {
    if (!date) return 'Não informado';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-6 w-6 text-blue-600" />
            Pré-visualização do Cliente
          </DialogTitle>
          <DialogDescription className="sr-only">
            Revise as informações do cliente antes de confirmar o cadastro.
          </DialogDescription>

        </DialogHeader>

        <div className="space-y-6">
          {/* Resumo do Cliente */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg text-blue-900">{formData.nome || 'Cliente'}</h3>
                <p className="text-sm text-blue-700">
                  {formData.cpf && `CPF: ${formatCpfCnpj(formData.cpf)}`}
                  {formData.email && ` • ${formData.email}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-blue-600 font-medium">
                  {Object.keys(formData).filter(key => formData[key] && formData[key] !== '').length} campos preenchidos
                </p>
                <p className="text-xs text-blue-600">
                  {documentos && documentos.length > 0 ? `${documentos.length} documentos anexados` : 'Nenhum documento anexado'}
                </p>
              </div>
            </div>
          </div>

          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Nome Completo
                </label>
                <p className="font-medium">{formData.nome || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <IdCard className="h-3 w-3" />
                  CPF
                </label>
                <p className="font-medium">{formatCpfCnpj(formData.cpf) || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Email
                </label>
                <p className="font-medium">{formData.email || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Telefone Principal
                </label>
                <p className="font-medium">{formatPhone(formData.tel1) || 'Não informado'}</p>
              </div>
              {formData.tel2 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    Telefone Secundário
                  </label>
                  <p className="font-medium">{formatPhone(formData.tel2)}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Data de Nascimento
                </label>
                <p className="font-medium">{formatDate(formData.dataNascimento)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <IdCard className="h-3 w-3" />
                  RG
                </label>
                <p className="font-medium">{formData.rg || 'Não informado'}</p>
              </div>
              {formData.dataEmissaoRg && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Data de Emissão do RG
                  </label>
                  <p className="font-medium">{formatDate(formData.dataEmissaoRg)}</p>
                </div>
              )}
              {formData.naturalidade && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Naturalidade</label>
                  <p className="font-medium">{formData.naturalidade}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nacionalidade</label>
                <p className="font-medium">{formData.nacionalidade || 'Brasileira'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5" />
                Endereço
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Logradouro</label>
                <p className="font-medium">{formData.logradouro || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Número</label>
                <p className="font-medium">{formData.numero || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Cidade</label>
                <p className="font-medium">{formData.cidade || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Estado</label>
                <p className="font-medium">{formData.estado || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">CEP</label>
                <p className="font-medium">{formData.cep || 'Não informado'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Bairro</label>
                <p className="font-medium">{formData.bairro || 'Não informado'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Informações Familiares */}
          {(formData.nomeMae || formData.nomePai || formData.nomeConjuge) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5" />
                  Informações Familiares
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Informações dos Pais */}
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-3 flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    Filiação
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.nomeMae && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Nome da Mãe</label>
                        <p className="font-medium">{formData.nomeMae}</p>
                      </div>
                    )}
                    {formData.nomePai && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Nome do Pai</label>
                        <p className="font-medium">{formData.nomePai}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informações do Estado Civil */}
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-3">Estado Civil</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <p className="font-medium capitalize">{formData.estadoCivil || 'Solteiro'}</p>
                    </div>
                    {formData.nomeConjuge && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Nome do Cônjuge</label>
                        <p className="font-medium">{formData.nomeConjuge}</p>
                      </div>
                    )}
                    {formData.cpfConjuge && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">CPF do Cônjuge</label>
                        <p className="font-medium">{formatCpfCnpj(formData.cpfConjuge)}</p>
                      </div>
                    )}
                    {formData.dataNascimentoConjuge && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Data de Nascimento do Cônjuge</label>
                        <p className="font-medium">{formatDate(formData.dataNascimentoConjuge)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dados Bancários */}
          {(formData.banco || formData.agencia || formData.conta || formData.tipoConta) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5" />
                  Dados Bancários
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {formData.banco && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      Banco
                    </label>
                    <p className="font-medium">{formData.banco}</p>
                  </div>
                )}
                {formData.agencia && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Agência</label>
                    <p className="font-medium">{formData.agencia}</p>
                  </div>
                )}
                {formData.conta && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Conta</label>
                    <p className="font-medium">{formData.conta}</p>
                  </div>
                )}
                {formData.tipoConta && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Tipo de Conta</label>
                    <p className="font-medium capitalize">{formData.tipoConta}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Informações Adicionais */}
          {(formData.atividadeDesenvolvida || formData.rendimentoTitular) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5" />
                  Informações Profissionais
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Atividade Desenvolvida</label>
                  <p className="font-medium">{formData.atividadeDesenvolvida || 'Não informado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Rendimento</label>
                  <p className="font-medium">{formData.rendimentoTitular || 'Não informado'}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status e Observações */}
          {formData.statusAtual && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5" />
                  Status e Observações
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status Atual</label>
                  <p className="font-medium">{formData.statusAtual}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documentos Anexados */}
          {documentos && documentos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Paperclip className="h-5 w-5" />
                  Documentos Anexados
                  <Badge variant="secondary" className="ml-2">
                    {documentos.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {documentos.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-blue-100">
                          {doc.tipo_documento === 'documento_com_foto' && <IdCard className="h-4 w-4 text-blue-600" />}
                          {doc.tipo_documento === 'comprovante_residencia' && <Home className="h-4 w-4 text-green-600" />}
                          {doc.tipo_documento === 'comprovante_pagamento' && <Receipt className="h-4 w-4 text-purple-600" />}
                          {doc.tipo_documento === 'declaracoes' && <FileText className="h-4 w-4 text-orange-600" />}
                          {doc.tipo_documento === 'comprovante_renda' && <Building className="h-4 w-4 text-indigo-600" />}
                          {!['documento_com_foto', 'comprovante_residencia', 'comprovante_pagamento', 'declaracoes', 'comprovante_renda'].includes(doc.tipo_documento) &&
                            <FileText className="h-4 w-4 text-gray-600" />
                          }
                        </div>
                        <div>
                          <p className="font-medium text-sm">{doc.nome_arquivo}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.tipo_documento === 'documento_com_foto' && 'Documento com Foto'}
                            {doc.tipo_documento === 'comprovante_residencia' && 'Comprovante de Residência'}
                            {doc.tipo_documento === 'comprovante_pagamento' && 'Comprovante de Pagamento'}
                            {doc.tipo_documento === 'declaracoes' && 'Declarações'}
                            {doc.tipo_documento === 'comprovante_renda' && 'Comprovante de Renda'}
                            {!['documento_com_foto', 'comprovante_residencia', 'comprovante_pagamento', 'declaracoes', 'comprovante_renda'].includes(doc.tipo_documento) &&
                              doc.tipo_documento
                            }
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Enviado em {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {getDocumentUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const url = await getDocumentUrl(doc.caminho_arquivo);
                              if (url) {
                                window.open(url, '_blank');
                              }
                            }}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            Ver
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (getDocumentUrl) {
                              const url = await getDocumentUrl(doc.caminho_arquivo);
                              if (url) {
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = doc.nome_arquivo;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }
                          }}
                          className="flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          Baixar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Indicador de Rascunho */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-100 text-blue-800">
                Pré-visualização
              </Badge>
              <span className="text-sm text-blue-700">
                Revise os dados antes de finalizar o cadastro
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onEdit} className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            Editar
          </Button>
          <Button onClick={onConfirm} className="flex items-center gap-2">
            <Check className="h-4 w-4" />
            Confirmar e Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
